import React, { useEffect, useState, useCallback, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import { Toast, ToastContainer } from "react-bootstrap";
import { apiCall } from "../../components/service/api-service";

//API Imports
import { getLeadsListApi } from "./leads-api";

//Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canCreate, canEdit, canDelete, canView } from "../common/permissionUtils";

//SVG Images
import Search from "../../assets/svg/search.svg";
import Filter from "../../assets/svg/filters.svg";
import Plus from "../../assets/svg/add.svg";
import ExcelImportIC from "../../assets/svg/excel-import-icon.svg";
import Dots from "../../assets/svg/groupdots.svg";
import Customize from "../../assets/svg/customize.svg";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";
import { ReactComponent as EditRow } from "../../assets/svg/edit.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";
import Copy from "../../assets/svg/copy_ics_a.svg";
// import DeleteModal from "../common/delete-modal";
import CommonFilter from "../common/commonfilter.js";
import ImportFileModal from "../common/importfile-modal.js";
import DeleteModal from "../common/delete-multi.js";
import {
  bulkDeleteLeadApi,
  deleteLeadApi,
} from "../quotation/quotation-api.js";
import {
  sanitizeInput,
  sanitizeFileName,
  validateFileSize,
  sanitizeEmail,
  sanitizePhoneNumber,
} from "../common/security-utils.js";
import RecordsPerPage from "../common/RecordsPerPage.jsx";
import Loader from "../common/Loader.jsx";
import AiChat from "../common/ai-chat.js";
import { askLeadChatApi, getAiProvidersApi } from "./leads-api";

// 🔒 VAPT Security Constants
const MAX_SEARCH_LENGTH = 200;
const MAX_ITEMS_PER_PAGE = 100;

// Persist list UI state (search, pagination, CommonFilter result) across
// navigation to a view page and back. Uses sessionStorage so it survives
// refresh but clears when the browser tab is closed.
const LEADS_LIST_STATE_KEY = "leadsListState";

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(LEADS_LIST_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const LeadsList = () => {
  const persisted = loadPersistedState();

  // 🔒 VAPT Security: Validate ID parameter
  const isValidId = (id) => {
    if (!id) return false;
    // Allow alphanumeric, hyphens, and underscores only
    return /^[a-zA-Z0-9_-]+$/.test(String(id));
  };

  //Filter
  const [showFilter, setShowFilter] = useState(false);
  // Result of the CommonFilter modal submission (a query-string fragment,
  // e.g. "&lead_source=...&lead_status=..."). Lifted into state (instead of
  // only living inside handleFilter's closure) so it survives pagination,
  // search, and navigating to a view page and back.
  const [activeFilters, setActiveFilters] = useState(persisted.activeFilters ?? "");
  const [showImport, setShowImport] = useState(false);

  const options = [
    "Lead Code",
    "Lead Name",
    "Company",
    "Email",
    "Phone number",
    "Lead Source",
    "Lead Status",
  ];

  const actionBtnStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    border: "0.6px solid #E5E5E5",
    background: "#fff",
    cursor: "pointer",
  };

  //Hovered Index
  const [hoveredIndex, setHoveredIndex] = useState(null);

  //Navigate
  const navigate = useNavigate();

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasCreatePermission = canCreate(rolePermissionList, "Leads");
  const hasEditPermission = canEdit(rolePermissionList, "Leads");
  const hasDeletePermission = canDelete(rolePermissionList, "Leads");
  const hasViewPermission = canView(rolePermissionList, "Leads");

  //Search
  const [search, setSearch] = useState(persisted.search ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(persisted.search ?? "");

  //Customize Table Dropdown
  const [showModal, setShowModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(() =>
    Object.fromEntries(options.map((label) => [label, true])),
  );

  const toggleColumn = (label) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };
  const anyColumnSelected = Object.values(selectedColumns).some(Boolean);
  const gridTemplate = `
  72px
  repeat(${options.filter((o) => selectedColumns[o]).length}, minmax(0, 1fr))
  96px
`;

  // Client-side sort — sorts only the currently loaded page of rows, no API
  // involvement. sortColumn is one of `options`; sortDirection toggles asc/desc;
  // clicking a different column resets to asc.
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSortClick = (label) => {
    if (sortColumn === label) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(label);
      setSortDirection("asc");
    }
  };

  //Delete
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  useEffect(() => {
    if (deleteId != null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const cancelDelete = () => {
    setIsOpenDelete(false);
  };
  const onSuccessDelete = (response) => {
    setDeleteId(null);
    // setSnackbarMessage(response.message);
    // setSnackbarStatus(response.success);
    // handleSnackbarShow();
    if (response.success === true) {
      setIsOpenDelete(false);
      getLeadsList();
    }
  };

  //Pagination
  const [itemOffset, setItemOffset] = useState(persisted.itemOffset ?? 0);
  const [itemsPerPage, setItemsPerPage] = useState(persisted.itemsPerPage ?? 10);
  const [pageCount, setPageCount] = useState(0);
  const [records, setRecords] = useState(0);

  const handlePageClick = useCallback(
    (event) => {
      setItemOffset(event.selected);
    },
    [setItemOffset],
  );

  //Get Leads List
  const [leadsList, setLeadsList] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const getLeadsList = async (filters = activeFilters) => {
    setListLoading(true);
    try {
      // 🔒 VAPT: Validate search length to prevent DoS
      const sanitizedSearch = debouncedSearch.slice(0, MAX_SEARCH_LENGTH);

      // 🔒 VAPT: Validate items per page to prevent resource exhaustion
      const validatedLimit = Math.min(itemsPerPage, MAX_ITEMS_PER_PAGE);

      const response = await getLeadsListApi(
        `?search=${encodeURIComponent(sanitizedSearch)}&limit=${validatedLimit}&skip=${itemOffset * validatedLimit}${filters ?? ""}`,
      );
      if (response.data.success === true) {
        // 🔒 VAPT: Validate numeric values
        const totalPages = Math.max(0, Math.min(response.data.total_pgs || 0, 10000));
        const totalRecords = Math.max(0, response.data.total_rec || 0);
        
        setPageCount(totalPages);
        setRecords(totalRecords);
        
        // 🔒 VAPT: Sanitize all lead data
        const formatted = formatLeadsData(response.data.leads || []);
        setLeadsList(formatted);
        setSelectedRows([]);
        setAllSelected(false);
      }
      return response.data;
    } catch (err) {
      // Error handled silently
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(handler);
  }, [search]);

  // Reset to page 1 when the search/page-size/filter actually changes — but
  // not on first mount, where itemOffset may have been restored from
  // sessionStorage and must be left alone.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setItemOffset(0);
  }, [debouncedSearch, itemsPerPage, activeFilters]);

  useEffect(() => {
    getLeadsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, itemsPerPage, itemOffset, activeFilters]);

  // Sort only the currently loaded page — client-side, no refetch.
  const sortedLeadsList = React.useMemo(() => {
    if (!sortColumn) return leadsList;
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...leadsList].sort((a, b) => {
      const aStr = (a[sortColumn] ?? "").toString().toLowerCase();
      const bStr = (b[sortColumn] ?? "").toString().toLowerCase();
      return aStr.localeCompare(bStr) * dir;
    });
  }, [leadsList, sortColumn, sortDirection]);

  // Persist list UI state so it is restored when returning from a view page.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        LEADS_LIST_STATE_KEY,
        JSON.stringify({
          search: debouncedSearch,
          itemOffset,
          itemsPerPage,
          activeFilters,
        }),
      );
    } catch {
      // ignore storage write errors (e.g. private mode quota)
    }
  }, [debouncedSearch, itemOffset, itemsPerPage, activeFilters]);

  const formatLeadsData = (rawLeads) => {
    return rawLeads.map((lead) => ({
      // 🔒 VAPT: Validate and sanitize ID
      id: isValidId(lead.id) ? lead.id : "",
      // 🔒 VAPT: Sanitize all displayed data to prevent XSS
      "Lead Name": sanitizeInput(lead.name ?? ""),
      "Lead Code": sanitizeInput(lead.lead_code ?? ""),
      Company: sanitizeInput(lead.company_name ?? ""),
      Email: sanitizeEmail(lead.email ?? ""),
      "Phone number": sanitizePhoneNumber(lead.contact_number1 ?? ""),
      "Lead Source": sanitizeInput(lead.lead_source ?? ""),
      "Lead Status": sanitizeInput(lead.lead_status ?? ""),
    }));
  };

  // state
  const [selectedRows, setSelectedRows] = useState([]);
  const [allSelected, setAllSelected] = useState(false);

  // handle checkbox logic
  const toggleAll = () => {
    if (allSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(leadsList.map((lead) => lead.id));
    }
    setAllSelected(!allSelected);
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  //Snackbar
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  // Copy to clipboard function
  const handleCopyLeadCode = (leadCode) => {
    navigator.clipboard.writeText(leadCode).then(() => {
      setSnackbarMessage(`✓ Copied: ${leadCode}`);
      setSnackbarStatus(true);
      handleSnackbarShow();
    }).catch(() => {
      setSnackbarMessage("✗ Failed to copy");
      setSnackbarStatus(false);
      handleSnackbarShow();
    });
  };

  const downloadBase64Excel = (base64String, filePrefix, sizeErrorMessage) => {
    if (!validateFileSize(base64String, 10)) {
      setSnackbarMessage(sizeErrorMessage);
      setSnackbarStatus(false);
      handleSnackbarShow();
      return false;
    }

    const fileName = sanitizeFileName(`${filePrefix}_${Date.now()}.xlsx`);
    const binary = atob(base64String);
    const array = [];
    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    const blob = new Blob([new Uint8Array(array)], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
    return true;
  };

  //Template Download
  const downloadTemplate = async () => {
    try {
      const response = await apiCall("GET", "lead_template_download");
      if (response?.data?.success === true && response?.data?.base64) {
        downloadBase64Excel(
          response.data.base64,
          "Lead_Template",
          "File size exceeds 10MB limit",
        );
      }

      setSnackbarMessage(sanitizeInput(response?.data?.message ?? ""));
      setSnackbarStatus(response?.data?.success === true);
      handleSnackbarShow();
      /*  setExcel(response.data.url); */
    } catch (err) {
      // Error handled silently
    } finally {
    }
  };

  const handleFilter = (filterData, type) => {
    setActiveFilters(filterData ?? "");
  };

  const uploadTicket = async (base64String) => {
    // Security: Validate input
    if (!base64String || typeof base64String !== "string") {
      setSnackbarMessage("Invalid file data");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }

    try {
      const data = {
        upload_file: base64String,
      };
      const response = await apiCall("POST", "leads_upload", data);
      if (response?.data?.success === true) {
        setSnackbarMessage(sanitizeInput(response.data.message ?? ""));
        setSnackbarStatus(true);
        handleSnackbarShow();
        setShowImport(false);
        if (response?.data?.base64) {
          downloadBase64Excel(
            response.data.base64,
            "Lead_Upload_Report",
            "Error file size exceeds 10MB limit",
          );
        }
        getLeadsList();
      } else {
        setSnackbarMessage(sanitizeInput(response?.data?.message ?? ""));
        setSnackbarStatus(false);
        handleSnackbarShow();
        if (response?.data?.base64) {
          downloadBase64Excel(
            response.data.base64,
            "Lead_Upload_Error",
            "Error file size exceeds 10MB limit",
          );
        }
      }
    } catch (err) {
      // Error handled silently
    }
  };

  const deleteSubIndustry = async () => {
    try {
      // 🔒 Permission check for delete
      if (!hasDeletePermission) {
        setSnackbarMessage("You don't have permission to delete records");
        setSnackbarStatus(false);
        handleSnackbarShow();
        return;
      }

      let response;
      const isBulk = selectedRows?.length > 0;

      // 🔒 VAPT: Validate inputs and IDs
      if (!isBulk && !deleteId) {
        setSnackbarMessage("Invalid delete operation");
        setSnackbarStatus(false);
        handleSnackbarShow();
        return;
      }

      // 🔒 VAPT: Validate all IDs before bulk delete
      if (isBulk) {
        const validIds = selectedRows.filter((id) => isValidId(id));
        if (validIds.length === 0) {
          setSnackbarMessage("No valid IDs selected");
          setSnackbarStatus(false);
          handleSnackbarShow();
          return;
        }
        response = await bulkDeleteLeadApi(validIds);
      } else {
        if (!isValidId(deleteId)) {
          setSnackbarMessage("Invalid ID");
          setSnackbarStatus(false);
          handleSnackbarShow();
          return;
        }
        response = await deleteLeadApi(deleteId);
      }

      const { success, message } = response?.data || {};

      // 🔒 VAPT: Sanitize message before display
      setSnackbarMessage(sanitizeInput(message ?? ""));
      setSnackbarStatus(!!success);
      handleSnackbarShow();

      if (!success) return;

      // 🔹 Common success cleanup
      setSelectedRows([]);
      setAllSelected(false);
      setDeleteId(null);
      setIsOpenDelete(false);
      getLeadsList();
    } catch (err) {
      setSnackbarMessage("Error deleting record.");
      setSnackbarStatus(false);
      handleSnackbarShow();
    }
  };

  return (
    <div
      style={{
        fontFamily: "'Euclid Circular B', sans-serif",
        padding: "16px",
        height: "100vh",
        width: "100%",
        overflowX: "hidden",
        backgroundColor: "#f8f9fb",
      }}
    >
      <div
        style={{
          padding: "16px",
          borderRadius: "16px",
          backgroundColor: "#FFFFFF",
        }}
      >
      <DeleteModal
        isOpen={isOpenDelete}
        title={
          selectedRows.length > 0
            ? `selected (${selectedRows.length}) leads`
            : "lead"
        }
        onClose={() => {
          setIsOpenDelete(false);
          setDeleteId(null);
        }}
        onDelete={deleteSubIndustry}
      />
      {isOpenDelete && (
        <DeleteModal
          deleteUrl={`web/lead/delete/${deleteId}`}
          onSuccess={onSuccessDelete}
          onCancel={cancelDelete}
        />
      )}
      <div style={{ marginBottom: "16px" }}>
        <div className="d-flex justify-content-between g-3">
          <div>
            {/* Leads count */}

            <div className="fs-14 fw-600 text-dkblue">
              Leads - {records}
            </div>
          </div>
          <div
            className="d-flex justify-content-end align-items-center"
            style={{ flexWrap: "wrap", gap: "12px" }}
          >
            {/* Search box */}

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "100px",
                  border: "0.8px solid #e5e5e5",
                  padding: "9px 16px",
                  backgroundColor: "#ffffff",
                  height: "36px",
                }}
              >
                <img src={Search} alt="search" style={{ width: "16px" }} />
                <input
                  type="search"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(sanitizeInput(e.target.value))}
                  style={{
                    border: "none",
                    outline: "none",
                    fontSize: "14px",
                    flex: 1,
                    backgroundColor: "transparent",
                  }}
                  className="no-focus-border"
                />
              </div>
              <img
                src={Filter}
                alt="Filter"
                style={{ height: "36px", width: "36px", cursor: "pointer" }}
                onClick={() => setShowFilter(true)}
              />
            </div>

            {/* ✅ COMMON FILTER */}
            <CommonFilter
              type="leads"
              open={showFilter}
              onClose={() => setShowFilter(false)}
              onSubmit={handleFilter}
            />

            {/* <Col xs={2} md={1} className="d-flex justify-content-center"> */}
            {/* ✅ COMMON FILTER */}
            <ImportFileModal
              type="leads"
              open={showImport}
              onClose={() => setShowImport(false)}
              onExport={downloadTemplate}
              onImport={(base64) => uploadTicket(base64)}
            />
            {/* </Col> */}

            {/* Create button with dropdown */}

            {
              <div className="d-flex justify-content-end align-items-center">
                {selectedRows.length === 0 && hasCreatePermission && (
                    <div
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "8px 16px",
                          borderRadius: "100px",
                          borderBottom: "3px solid #1723C2",
                          background:
                            "linear-gradient(180deg, #6971DD 0%, #3D4AF5 100%)",
                          height: "40px",
                          width: "fit-content",
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                          onClick={() => navigate("/leads/create")}
                        >
                          <img src={Plus} style={{ width: "16px" }} alt="Plus" />
                          <div className="fs-15 fw-600 text-white">Create</div>
                        </div>
                      {/* <div
                      onClick={() => setOpen(!open)}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <div
                        style={{
                          width: "1px",
                          height: "40px",
                          backgroundColor: "#ffffff",
                        }}
                      ></div>
                      <div style={{ paddingLeft: "16px" }}>
                        <img
                          src={Dropdown}
                          style={{ width: "10px" }}
                          alt="Dropdown"
                        />
                      </div>
                    </div> */}
                      </div>

                      {/* {open && (
                      <div
                        style={{
                          position: "absolute",
                          top: "50px",
                          backgroundColor: "#ffffff",
                          padding: "16px",
                          borderRadius: "16px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          zIndex: 1000,
                          width: "100%",
                        }}
                      >
                        <div style={{ padding: "8px 0" }}>Lead</div>
                        <div style={{ padding: "8px 0" }}>Contact</div>
                        <div style={{ padding: "8px 0" }}>Account</div>
                        <div style={{ padding: "8px 0" }}>Deal</div>
                      </div>
                    )} */}
                    </div>
                  )}
                {selectedRows.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      padding: "0 20px",
                      borderRadius: "50px",
                      borderBottom: "3px solid #B31717",
                      background:
                        "linear-gradient(180deg, #FF7A7A 0%, #D61916 100%)",
                      height: "40px",
                      width: "auto",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setIsOpenDelete(true);
                    }}
                  >
                    <div className="fs-15 fw-600 text-white">Delete ({selectedRows.length})</div>
                  </div>
                )}
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                    marginLeft: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "8px 16px",
                      borderRadius: "100px",
                      border: "1px solid #3D4AF5",
                      height: "36px",
                      width: "fit-content",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                      onClick={() => setShowImport(true)}
                    >
                      <img
                        src={ExcelImportIC}
                        style={{ width: "16px" }}
                        alt="Import"
                      />
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "16px",
                          color: "#3D4AF5",
                        }}
                      >
                        Import
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      </div>

      <div style={{ marginBottom: "16px" }}>
        <Row>
          <Col sm={12}>
            <div
              style={{
                borderRadius: "16px",
                border: "1px solid #ebebeb",
                backgroundColor: "#ffffff",
                overflow: "hidden",
              }}
              className="table-scroll-container"
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
              {/* Header */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: gridTemplate,
                  alignItems: "center",
                  width: "100%",
                  borderBottom: "1px solid #ebebeb",
                  // background: "#f9fafb",
                }}
              >
                {/* Select All */}
                <div
                  style={{
                    padding: "10px 24px",
                    borderRight: "1px solid #ebebeb",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                  />
                </div>

                {/* Column Headers */}
                {options.map(
                  (label, index) =>
                    selectedColumns[label] && (
                      <div
                        key={index}
                        onClick={() => handleSortClick(label)}
                        title={`Sort by ${label}`}
                        style={{
                          padding: "10px 16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderRight: "1px solid #ebebeb",
                          minWidth: 0, // 🔴 critical for ellipsis
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
                          <span
                            className="fs-12 fw-600 text-ltgrey"
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {label.toUpperCase()}
                          </span>
                          <span className="fs-12 fw-600" style={{ flexShrink: 0, color: sortColumn === label ? "#3D4AF5" : "#c4c9d4" }}>
                            {sortColumn === label && sortDirection === "desc" ? "▼" : "▲"}
                          </span>
                        </div>
                        <img src={Dots} alt="dots" onClick={(e) => e.stopPropagation()} />
                      </div>
                    ),
                )}

                {/* Customize */}
                <div
                  onClick={() => setShowModal(true)}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <img src={Customize} alt="customize" />
                </div>
              </div>

              {/* Data Rows */}
              {listLoading ? (
                <div style={{ width: "100%", padding: "40px 0" }}>
                  <Loader height="120px" />
                </div>
              ) : sortedLeadsList.length === 0 ? (
                <div style={{ width: "100%", textAlign: "center", padding: "40px 0", color: "#8790A1" }}>
                  No leads found
                </div>
              ) : (
                sortedLeadsList.map((lead, rowIndex) => (
                <div
                  key={lead.id}
                  onMouseEnter={() => setHoveredIndex(rowIndex)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => {
                    if (hasViewPermission) {
                      navigate("/leads/view", { state: { id: lead.id } });
                    }
                  }}
                  style={{
                    display: "grid",
                    gridTemplateColumns: gridTemplate,
                    alignItems: "center",
                    borderBottom: "1px solid #ebebeb",
                    background: "#fff",
                    cursor: hasViewPermission ? "pointer" : "not-allowed",
                  }}
                >
                  {/* Checkbox */}
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      padding: "10px 24px",
                      borderRight: "1px solid #ebebeb",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(lead.id)}
                      onChange={() => toggleRow(lead.id)}
                      disabled={!anyColumnSelected}
                    />
                  </div>

                  {/* Dynamic columns */}
                  {options.map(
                    (label, colIndex) =>
                      selectedColumns[label] && (
                        <div
                          key={colIndex}
                          title={lead[label]}
                          className="text-dkblue fs-14 fw-400"
                          style={{
                            padding: "10px 16px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            borderRight: "1px solid #ebebeb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "8px",
                          }}
                        >
                          <div style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                            {lead[label] || "-"}
                          </div>
                          {label === "Lead Code" && lead[label] && (
                            <img
                              src={Copy}
                              alt="Copy"
                              style={{
                                width: "16px",
                                height: "16px",
                                cursor: "pointer",
                                flexShrink: 0,
                                opacity: 0.6,
                                transition: "opacity 0.2s",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyLeadCode(lead[label]);
                              }}
                              onMouseEnter={(e) => (e.target.style.opacity = "1")}
                              onMouseLeave={(e) => (e.target.style.opacity = "0.6")}
                              title="Copy lead code"
                            />
                          )}
                        </div>
                      ),
                  )}

                  {/* Actions */}
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "12px",
                      opacity: hoveredIndex === rowIndex ? 1 : 0,
                      transition: "opacity 0.15s ease",
                    }}
                  >
                    {/* Edit Button */}
                    {hasEditPermission && (
                      <div
                        onClick={() =>
                          navigate("/leads/create", { state: { id: lead.id } })
                        }
                        style={actionBtnStyle}
                        title="Edit Lead"
                      >
                        <EditRow />
                      </div>
                    )}

                    {/* Delete Button */}
                    {hasDeletePermission && (
                      <div
                        onClick={() => setDeleteId(lead.id)}
                        style={actionBtnStyle}
                        title="Delete Lead"
                      >
                        <DeleteRow />
                      </div>
                    )}
                  </div>
                </div>
                ))
              )}

              {showModal && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#fff",
                      padding: "24px",
                      borderRadius: "12px",
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                      width: "300px",
                      maxHeight: "80vh",
                      overflowY: "auto",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: "16px",
                        marginBottom: "16px",
                      }}
                    >
                      Select Columns
                    </div>
                    {options.map((label, index) => (
                      <label
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "10px",
                          fontSize: "14px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!selectedColumns[label]}
                          onChange={() => toggleColumn(label)}
                          style={{ marginRight: "8px" }}
                        />
                        {label}
                      </label>
                    ))}
                    <button
                      onClick={() => setShowModal(false)}
                      style={{
                        marginTop: "16px",
                        width: "100%",
                        padding: "10px",
                        borderRadius: "8px",
                        border: "none",
                        backgroundColor: "#3D4AF5",
                        color: "#fff",
                        fontWeight: "500",
                        cursor: "pointer",
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div>
        <Row>
          <Col>
            <RecordsPerPage
              value={itemsPerPage}
              onChange={(event) => {
                setItemsPerPage(event.target.value);
              }}
            />
          </Col>
          <Col>
            <div className="d-flex justify-content-end">
              <ReactPaginate
                previousLabel={<img src={lar} className="ar3" alt="previous" />}
                nextLabel={<img src={rar} className="ar3" alt="next" />}
                breakLabel={"..."}
                pageCount={pageCount + 1}
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                onPageChange={handlePageClick}
                forcePage={itemOffset}
                containerClassName={"pagination"}
                activeClassName={"active"}
              />
            </div>
          </Col>
        </Row>

        <ToastContainer position="top-end">
          <Toast
            className="d-inline-block m-1"
            bg={snackbarStatus === true ? "success" : "danger"}
            show={showSnackbar}
            onClose={handleSnackbarClose}
            delay={3000}
            autohide
          >
            <Toast.Body style={{ color: "#ffffff" }}>
              {snackbarMessage}
            </Toast.Body>
          </Toast>
        </ToastContainer>
      </div>
      <AiChat
        askApi={askLeadChatApi}
        providersApi={getAiProvidersApi}
        title="Ask about leads"
        hint="Try: “which leads from this month have no activity logged?” or “who are my highest scoring leads in manufacturing?”"
      />
      </div>
   </div>
  );
};

export default LeadsList;
